import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';
import {ReactFlowInstance, useReactFlow} from 'reactflow';

import {
  createRouterNode,
  createStopNode,
  createTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';

import {ProcessFlowchartNodeType} from '../processFlowchartConfigEditorConstants';
import {
  NodeTypeToStaticNodeDataMap,
  FlowchartNodeTypeToReactflowNodeMap,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';

interface Position {
  x: number;
  y: number;
}

export const DEFAULT_NODE_PROPS = {
  selected: false,
  isConnectable: false,
  xPos: 0,
  yPos: 0,
  zIndex: 0,
};

export const nodeStyles = StyleSheet.create({
  draggableNode: {cursor: 'grab', margin: 6},
  draggingNode: {cursor: 'grabbing', margin: 6},
});

export const NODE_TYPE_TO_CREATE_NODE_FUNCTION: {
  [FlowchartNodeType in ProcessFlowchartNodeType]: (
    staticData: NodeTypeToStaticNodeDataMap[FlowchartNodeType],
    position: Position,
    options: FlowchartOptions
  ) => FlowchartNodeTypeToReactflowNodeMap[FlowchartNodeType];
} = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: createTaskNode,
  [ProcessFlowchartNodeType.ROUTER_NODE]: createRouterNode,
  [ProcessFlowchartNodeType.STOP_NODE]: createStopNode,
};

// Offset to ensure that nodes are dropped in the correct location on the canvas, determined via trial and error
const DROP_X_OFFSET = -70;
const DROP_Y_OFFSET = 85;

export function getPosition(
  event: React.DragEvent<HTMLDivElement>,
  reactFlowInstance: ReactFlowInstance
): Position {
  const target = event.target as HTMLDivElement;
  const boundingRect = target.getBoundingClientRect();
  const zoomScale = reactFlowInstance.getZoom();
  // When reactflow is upgraded, we can use screenToFlowPosition: https://reactflow.dev/whats-new/2023-11-10#rename-usereactflowproject-to-usereactflowscreentoflowposition
  const position = reactFlowInstance.project({
    x: event.clientX - boundingRect.left + DROP_X_OFFSET * zoomScale,
    y: event.clientY - boundingRect.top + DROP_Y_OFFSET * zoomScale,
  });

  return position;
}

export function DraggableNode(props: {
  addNode: (position: Position) => void;
  nodeRenderer: (props: {dragging: boolean}) => JSX.Element;
}): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const reactFlowInstance = useReactFlow();

  const dragStartHandler = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const position = getPosition(event, reactFlowInstance);

      props.addNode(position);
    },
    [props, reactFlowInstance]
  );

  const dragEndHandler = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      setIsDragging(false);
      onDrop(event);
    },
    [onDrop]
  );

  return (
    <div
      className={css(isDragging ? nodeStyles.draggingNode : nodeStyles.draggableNode)}
      draggable={true}
      onDragStart={dragStartHandler}
      onDragEnd={dragEndHandler}
    >
      <props.nodeRenderer dragging={isDragging} />
    </div>
  );
}
